import { useCallback, useState, useMemo } from 'react'
import {
  DealTableRow,
  GenericSortHeader,
  dealTableHeadRowClass,
  useDealTableInteraction,
  useGenericTableSort,
} from '@/components/shared/DealTable'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { usePortfolioAnalyticsBook } from '@/hooks/useDemoDataAccess'
import { CHART_TOOLTIP_PROPS, chartAxisProps, chartGridProps, chartSeriesColor } from '@/config/chartTheme'

const fmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(2)}B` : `$${v.toFixed(1)}M`
const pct = (v: number, d = 1) => `${(v * 100).toFixed(d)}%`

type StressRow = {
  id: string
  property: string
  type: string
  origValue: number
  origCapRate: number
  stressedCapRate: number
  stressedValue: number
  valueLoss: number
  valueLossPct: number
  origLtv: number
  stressedLtv: number
  covenantBreach: boolean
}

export default function CapRateStressTest() {
  const deals = usePortfolioAnalyticsBook().complete
  const [expansion, setExpansion] = useState(50) // basis points
  const { focusedDealId, activateDeal } = useDealTableInteraction()

  const analysis = useMemo((): StressRow[] => {
    return deals.map((d) => {
      const origValue = d.propertyValue ?? d.loanAmount / (d.ltv || 0.70)
      const noi = d.cashFlows.prospectiveNOI
      const origCapRate = noi / origValue
      const stressedCapRate = origCapRate + expansion / 10000
      const stressedValue = noi / stressedCapRate
      const valueLoss = origValue - stressedValue
      const valueLossPct = valueLoss / origValue
      const stressedLtv = d.loanAmount / stressedValue
      const covenantBreach = stressedLtv > 0.75

      return {
        id: d.id,
        property: d.property,
        type: d.type,
        origValue,
        origCapRate,
        stressedCapRate,
        stressedValue,
        valueLoss,
        valueLossPct,
        origLtv: d.ltv,
        stressedLtv,
        covenantBreach,
      }
    })
  }, [deals, expansion])

  const getStressRowValue = useCallback((row: StressRow, columnIndex: number) => {
    switch (columnIndex) {
      case 0:
        return row.property
      case 1:
        return row.type
      case 2:
        return row.origCapRate
      case 3:
        return row.stressedCapRate
      case 4:
        return row.origValue
      case 5:
        return row.stressedValue
      case 6:
        return row.valueLossPct
      case 7:
        return row.stressedLtv
      case 8:
        return row.covenantBreach ? 1 : 0
      default:
        return ''
    }
  }, [])

  const { sortColumn, sortDir, toggleSort, sortedRows: sortedAnalysis } = useGenericTableSort(
    analysis,
    getStressRowValue,
    6,
  )

  // Chart data: portfolio value at different expansion levels
  const chartData = useMemo(() => {
    const points = []
    for (let bp = 0; bp <= 200; bp += 25) {
      let totalValue = 0
      let breaches = 0
      deals.forEach((d) => {
        const origValue = d.propertyValue ?? d.loanAmount / (d.ltv || 0.70)
        const noi = d.cashFlows.prospectiveNOI
        const origCap = noi / origValue
        const sv = noi / (origCap + bp / 10000)
        totalValue += sv
        if (d.loanAmount / sv > 0.75) breaches++
      })
      points.push({ bp: `+${bp}bp`, value: totalValue, breaches })
    }
    return points
  }, [])

  const totalOrigValue = analysis.reduce((s, a) => s + a.origValue, 0)
  const totalStressedValue = analysis.reduce((s, a) => s + a.stressedValue, 0)
  const totalLoss = totalOrigValue - totalStressedValue
  const breachCount = analysis.filter((a) => a.covenantBreach).length

  return (
    <div className="space-y-4">
      {/* Slider */}
      <div className="panel">
        <div className="panel-body">
          <div className="flex items-center gap-4">
            <span className="text-[11px] text-stone-400 whitespace-nowrap">Cap Rate Expansion:</span>
            <input
              type="range"
              min={0}
              max={200}
              step={5}
              value={expansion}
              onChange={(e) => setExpansion(Number(e.target.value))}
              className="flex-1 accent-amber-400"
            />
            <span className="text-sm font-mono font-bold text-amber-300 w-16 text-right">+{expansion}bp</span>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-3">
        <div className="metric-cell">
          <div className="metric-cell-label">Portfolio Value (Orig)</div>
          <div className="metric-cell-value">{fmt(totalOrigValue)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Portfolio Value (Stressed)</div>
          <div className="metric-cell-value text-red-400">{fmt(totalStressedValue)}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Value Decline</div>
          <div className="metric-cell-value text-red-400">-{fmt(totalLoss)} ({pct(totalLoss / totalOrigValue)})</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Covenant Breaches</div>
          <div className={`metric-cell-value ${breachCount > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{breachCount} / {analysis.length}</div>
        </div>
      </div>

      {/* Chart */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Portfolio Value vs Cap Rate Expansion</span>
        </div>
        <div className="panel-body">
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ left: 8, right: 16, top: 8, bottom: 4 }}>
                <CartesianGrid {...chartGridProps()} />
                <XAxis dataKey="bp" {...chartAxisProps()} />
                <YAxis
                  {...chartAxisProps()}
                  tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}B`}
                />
                <Tooltip
                  {...CHART_TOOLTIP_PROPS}
                  formatter={(v: number) => [fmt(v), 'Portfolio Value']}
                />
                <Line type="monotone" dataKey="value" stroke={chartSeriesColor(0)} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Deal-level table */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Deal-Level Impact at +{expansion}bp</span>
        </div>
        <div className="overflow-x-auto">
          <table className="dense-table">
            <thead>
              <tr className={dealTableHeadRowClass()}>
                <GenericSortHeader label="Property" columnIndex={0} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Type" columnIndex={1} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Orig Cap" columnIndex={2} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Stressed Cap" columnIndex={3} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Orig Value" columnIndex={4} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Stressed Value" columnIndex={5} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Loss" columnIndex={6} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="LTV (Stressed)" columnIndex={7} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="right" className="font-mono text-[9px] px-1.5 py-1" />
                <GenericSortHeader label="Breach" columnIndex={8} sortColumn={sortColumn} sortDir={sortDir} onSort={toggleSort} align="center" className="font-mono text-[9px] px-1.5 py-1" />
              </tr>
            </thead>
            <tbody>
              {sortedAnalysis.map((a) => (
                <DealTableRow
                  key={a.id}
                  dealId={a.id}
                  isFocused={focusedDealId === a.id}
                  onActivate={activateDeal}
                  openProfileOnClick={false}
                  className={a.covenantBreach ? 'bg-red-400/[0.03]' : ''}
                >
                  <td className="font-medium text-stone-200 whitespace-nowrap">{a.property}</td>
                  <td className="text-stone-400">{a.type}</td>
                  <td className="text-right font-mono">{pct(a.origCapRate)}</td>
                  <td className="text-right font-mono">{pct(a.stressedCapRate)}</td>
                  <td className="text-right font-mono">{fmt(a.origValue)}</td>
                  <td className="text-right font-mono">{fmt(a.stressedValue)}</td>
                  <td className="text-right font-mono text-red-400">-{pct(a.valueLossPct)}</td>
                  <td className={`text-right font-mono ${a.stressedLtv > 0.75 ? 'text-red-400 font-bold' : 'text-stone-200'}`}>
                    {pct(a.stressedLtv)}
                  </td>
                  <td className="text-center">
                    {a.covenantBreach ? (
                      <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                    ) : (
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                    )}
                  </td>
                </DealTableRow>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
